/**
 * The measured model: the invisible, measured shell of a hall that the app snaps to, measures
 * against, hides props behind and catches shadows on. Written by the pipeline
 * (`mawkeb_pipeline/model.py`) from the floor plan, the LiDAR scan or the synthetic test hall.
 *
 * Metres in the hall frame: origin on the floor at the back-left corner (standing at the back wall
 * facing the stage), x across from the left wall, y from the back wall towards the stage, z up.
 * Outlines run anticlockwise seen from above. Every size carries a state saying how sure it is.
 */

export type Vec2 = [number, number];
export type Vec3 = [number, number, number];

export type SizeState = 'labelled' | 'scaled' | 'catalogue' | 'measured' | 'lidar' | 'assumed' | 'synthetic';

export const SIZE_STATES: readonly SizeState[] = ['labelled', 'scaled', 'catalogue', 'measured', 'lidar', 'assumed', 'synthetic'];

/** A surface material. `acoustic` is filled in for the sound work in version two. */
export interface Material {
  name: string;
  /** sRGB hex used when the model itself is drawn (no scan yet, or the plan view). */
  colour: string;
  acoustic: null | { absorption: Record<string, number>; source: string };
}

export interface Band {
  z0: number;
  z1: number;
  material: string;
  state: SizeState;
}

export interface Wall {
  id: string;
  name: string;
  /** The interior face at floor level, with the room on the left going from a to b. */
  a: Vec2;
  b: Vec2;
  height: number;
  material: string;
  /** Horizontal bands of other materials (wainscot, windows), bottom to top. */
  bands: Band[];
  state: SizeState;
}

export type SolidKind = 'stage' | 'column' | 'room' | 'steps' | 'platform' | 'other';

export interface Solid {
  id: string;
  name: string;
  kind: SolidKind;
  outline: Vec2[];
  z0: number;
  z1: number;
  top: string;
  side: string;
  /** Someone in walk view can stand on its top. */
  walkable: boolean;
  state: SizeState;
}

export interface Opening {
  id: string;
  name: string;
  /** 'opening' is a doorway with no door, or the foot of a stair. */
  kind: 'door' | 'window' | 'opening';
  wall: string;
  /** Distances along the wall from its start (a). */
  from: number;
  to: number;
  z0: number;
  z1: number;
  material: string;
  state: SizeState;
}

export interface Line {
  id: string;
  name: string;
  kind: 'divider' | 'guide';
  a: Vec2;
  b: Vec2;
  state: SizeState;
}

/** A distance someone wrote down (the architect, the tape) that the model is checked against. */
export interface Reference {
  id: string;
  label: string;
  a: Vec3;
  b: Vec3;
  /** The distance as written, in metres, and as it was written. */
  value: number;
  written: string;
  state: SizeState;
}

export type Ceiling =
  | { kind: 'flat'; height: number; material: string; state: SizeState }
  | { kind: 'gable'; eave: number; ridge: number; axis: 'x' | 'y'; material: string; state: SizeState };

export interface MeasuredModel {
  format: 'mawkeb-measured-model';
  version: 1;
  hall: { id: string; name: string; site?: string };
  variant: 'plan' | 'synthetic' | 'lidar';
  source: string;
  frame: string;
  bounds: { min: Vec3; max: Vec3 };
  floor: { outline: Vec2[]; z: number; material: string; state: SizeState };
  ceiling: Ceiling;
  walls: Wall[];
  solids: Solid[];
  openings: Opening[];
  lines: Line[];
  references: Reference[];
  materials: Record<string, Material>;
}

export class ModelError extends Error {
  override name = 'ModelError';
}

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isVec = (v: unknown, n: number): boolean => Array.isArray(v) && v.length === n && v.every(isNum);

/** Checks a model read from the network before anything relies on it; throws a ModelError naming the fault. */
export function checkModel(raw: unknown): MeasuredModel {
  const fail = (msg: string): never => {
    throw new ModelError(`measured model: ${msg}`);
  };
  if (!raw || typeof raw !== 'object') fail('not an object');
  const m = raw as MeasuredModel;
  if (m.format !== 'mawkeb-measured-model') fail(`unexpected format ${String(m.format)}`);
  if (m.version !== 1) fail(`version ${String(m.version)} is newer than this app understands`);
  if (!m.hall?.id) fail('no hall id');
  if (!isVec(m.bounds?.min, 3) || !isVec(m.bounds?.max, 3)) fail('bounds missing');
  if (!Array.isArray(m.floor?.outline) || m.floor.outline.length < 3 || !m.floor.outline.every((p) => isVec(p, 2))) {
    fail('floor outline missing');
  }
  const mats = m.materials ?? fail('materials missing');
  const needMaterial = (id: string, where: string) => {
    if (!mats[id]) fail(`${where} uses material "${id}", which isn't listed`);
  };
  needMaterial(m.floor.material, 'floor');
  needMaterial(m.ceiling?.material, 'ceiling');
  if (m.ceiling.kind === 'flat' && !isNum(m.ceiling.height)) fail('ceiling height missing');
  if (m.ceiling.kind === 'gable' && (!isNum(m.ceiling.eave) || !isNum(m.ceiling.ridge))) fail('ceiling heights missing');
  for (const w of m.walls ?? fail('walls missing')) {
    if (!isVec(w.a, 2) || !isVec(w.b, 2) || !isNum(w.height)) fail(`wall ${w.id} is incomplete`);
    needMaterial(w.material, `wall ${w.id}`);
    for (const b of w.bands ?? []) needMaterial(b.material, `wall ${w.id}`);
  }
  for (const s of m.solids ?? fail('solids missing')) {
    if (!Array.isArray(s.outline) || s.outline.length < 3 || !s.outline.every((p) => isVec(p, 2))) fail(`${s.id} outline missing`);
    if (!isNum(s.z0) || !isNum(s.z1) || s.z1 <= s.z0) fail(`${s.id} heights are wrong`);
    needMaterial(s.side, s.id);
    needMaterial(s.top, s.id);
  }
  for (const o of m.openings ?? fail('openings missing')) needMaterial(o.material, o.id);
  for (const r of m.references ?? fail('references missing')) {
    if (!isVec(r.a, 3) || !isVec(r.b, 3) || !isNum(r.value)) fail(`reference ${r.id} is incomplete`);
  }
  if (!Array.isArray(m.lines)) fail('lines missing');
  return m;
}

/** Height of the floor someone stands on at (x, y): the top of a walkable solid, else the hall floor. */
export function standingHeight(model: MeasuredModel, x: number, y: number, inside: (p: Vec2, poly: Vec2[]) => boolean): number {
  let z = model.floor.z;
  for (const s of model.solids) {
    if (s.walkable && s.z1 > z && inside([x, y], s.outline)) z = s.z1;
  }
  return z;
}

export function ceilingHeightAt(model: MeasuredModel, x: number, y: number): number {
  const c = model.ceiling;
  if (c.kind === 'flat') return c.height;
  const { min, max } = model.bounds;
  const [lo, hi, v] = c.axis === 'y' ? [min[0], max[0], x] : [min[1], max[1], y];
  const mid = (lo + hi) / 2;
  const half = (hi - lo) / 2 || 1;
  return c.ridge - (c.ridge - c.eave) * Math.min(1, Math.abs(v - mid) / half);
}
